import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useUser } from '@/auth/useAuth'
import { can } from '@/auth/rbac'
import { priorityLabel } from '@/i18n/labels'
import { formatDate, formatDateTime } from '@/lib/format'
import { Button, Card, ErrorState, LoadingState, OrderStatusChip, RiskChip, cn } from '@/ui'
import { QCTab } from '@/features/quality/QCTab'
import { ReportsTab } from '@/features/reports/ReportsTab'
import { OrderEditSheet } from './OrderEditSheet'
import { OrderLifecycleBar } from './OrderLifecycleBar'
import { StagesTab } from './tabs/StagesTab'
import { HistoryTab } from './tabs/HistoryTab'
import { useOrder } from './queries'

type Tab = 'stages' | 'qc' | 'reports' | 'history'
const TABS: { key: Tab; label: string }[] = [
  { key: 'stages', label: 'Этапы' },
  { key: 'qc', label: 'ОТК' },
  { key: 'reports', label: 'Отчёты' },
  { key: 'history', label: 'История' },
]

export function OrderDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const user = useUser()
  const { data: order, isLoading, isError, error, refetch } = useOrder(id)
  const [tab, setTab] = useState<Tab>('stages')
  const [editOpen, setEditOpen] = useState(false)

  if (isLoading) return <LoadingState />
  if (isError || !order) return <ErrorState error={error} onRetry={() => void refetch()} />

  const canEdit = order.lifecycle_status === 'DRAFT' && can(user.role, 'orders.manage')

  return (
    <div className="space-y-3">
      <button onClick={() => navigate('/orders')} className="text-sm text-tg-link active:opacity-70">
        ← К заказам
      </button>

      <Card className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-tg-text">{order.product_name}</h1>
            <p className="text-xs text-tg-hint">
              № {order.order_number} · {order.client_name}
            </p>
          </div>
          <RiskChip level={order.risk_level} />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <OrderStatusChip status={order.lifecycle_status} />
          <span className="text-xs text-tg-hint">Срок: {formatDate(order.due_at)}</span>
          <span className="text-xs text-tg-hint">Кол-во: {order.quantity}</span>
          <span className="text-xs text-tg-hint">{priorityLabel[order.priority]}</span>
        </div>

        {order.description && <p className="mt-2 text-sm text-tg-text">{order.description}</p>}

        {order.started_at && (
          <p className="mt-2 text-xs text-tg-hint">Запущен: {formatDateTime(order.started_at)}</p>
        )}
        {order.completed_at && (
          <p className="text-xs text-tg-hint">Завершён: {formatDateTime(order.completed_at)}</p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canEdit && (
            <Button size="md" variant="secondary" onClick={() => setEditOpen(true)}>
              Редактировать
            </Button>
          )}
          <OrderLifecycleBar order={order} />
        </div>
      </Card>

      <div className="flex gap-1 rounded-xl bg-tg-secondary-bg p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              'flex-1 rounded-lg py-2 text-sm font-medium transition',
              tab === t.key ? 'bg-tg-bg text-tg-text shadow-sm' : 'text-tg-hint',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div>
        {tab === 'stages' && <StagesTab orderId={id} />}
        {tab === 'qc' && <QCTab orderId={id} />}
        {tab === 'reports' && <ReportsTab orderId={id} />}
        {tab === 'history' && <HistoryTab orderId={id} />}
      </div>

      {canEdit && <OrderEditSheet order={order} open={editOpen} onClose={() => setEditOpen(false)} />}
    </div>
  )
}
